import * as THREE from 'three';
import { szene, kamera, renderer, licht } from './index'
import Terrain from './Terrain';
import Spieler from './Spieler';
import ÄpfelSpawner from './ÄpfelSpawner';
import HUD from './HUD';
import AudioSpieler from './AudioSpieler';
import Stats from 'stats.js';

export default class SpielFluss {
	private spieler: Spieler;
	private terrain: Terrain;
	public äpfelSpawner: ÄpfelSpawner | null = null;
	private hud: HUD;
	private audioSpieler: AudioSpieler;
	private punkteZahl = 0;
	private schleifeSollGestopptWerden = false;
	private spielerHatVerloren = false;
	public istErstesSpiel = true;
	// Spiele performance fürs Debugging
	private stats = new Stats();
	constructor(spieler: Spieler, terrain: Terrain, hud: HUD, audioSpieler: AudioSpieler) {
		this.spieler = spieler;
		this.terrain = terrain;
		this.hud = hud;
		this.äpfelSpawner = new ÄpfelSpawner(terrain, this.änderePunkteZahl);
		this.audioSpieler = audioSpieler;
	}
	// Nutze Pheilschriftschreibweise, damit "this" für die Klasse steht

	public initializiereSpiel = () => {
		// Kreeire wiederverwendbares Terrain
		this.terrain.kreeireTerrain();
		szene.add(this.terrain.instanziierteMeshe);
		// Stats
		this.stats.showPanel(0);
		// Licht
		licht.position.set(this.terrain.breite / 2, this.terrain.höhe / 2, this.terrain.dicke / 2);
		szene.add(licht);
		// Damit die Initialization nur einmal ausgeführt wird
		this.istErstesSpiel = false;
	}
	public starteSpiel = () =>  {
		this.spielerHatVerloren = false;
		this.hud.aufSpielStart();
		const neueTerrainGröße = new THREE.Vector3(this.terrain.breite, this.terrain.höhe, this.terrain.dicke);
		[...this.hud.terrainSchiebeRegler].map((terrainSchiebeRegler) => {
			if(terrainSchiebeRegler.id === 'terrain-breite' && this.terrain.breite !== parseInt(terrainSchiebeRegler.value)) {
				neueTerrainGröße.x = parseInt(terrainSchiebeRegler.value);
			}
			else if(terrainSchiebeRegler.id === 'terrain-höhe' && this.terrain.höhe !== parseInt(terrainSchiebeRegler.value)) {
				neueTerrainGröße.y = parseInt(terrainSchiebeRegler.value);
			}
			else if(terrainSchiebeRegler.id === 'terrain-dicke' && this.terrain.dicke !== parseInt(terrainSchiebeRegler.value)) {
				neueTerrainGröße.z = parseInt(terrainSchiebeRegler.value);
			}
		});
		if(!neueTerrainGröße.equals(new THREE.Vector3(this.terrain.breite, this.terrain.höhe, this.terrain.dicke))) {
			this.terrain.setzeGrößeNeu(neueTerrainGröße);
			// aktualisiere Apfel Spawnbereich und Kamera
			this.äpfelSpawner!.spawnBereich = this.äpfelSpawner!.erstelleSpawnBereich(this.terrain);
			kamera.far = this.terrain.breite + this.terrain.höhe + this.terrain.dicke;
			kamera.updateProjectionMatrix();
		}
		// Setze Farbe von Spieler
		this.spieler.körperFarbe = this.hud.spielerFarbe.value;
		this.spieler.material.dispose();
		this.spieler.material = new THREE.MeshBasicMaterial({color: this.spieler.körperFarbe});
		// Entferne alle Äpfel
		this.äpfelSpawner!.äpfelListe.map((apfel) => szene.remove(apfel));
		this.äpfelSpawner!.äpfelListe = [];
		// Setze die Länge der Schlange und ihre anderen Attribute wieder neu
		this.spieler.richtungsVektor.set(0, 0, -1);
		this.spieler.bewegungsInterval = 750;
		this.spieler.blockListe.map((spielerBlock) => szene.remove(spielerBlock.mesh));
		this.spieler.blockListe = [
			{
			mesh: this.spieler.kreeireSpielerMesh(),
			richtungsVektor: this.spieler.richtungsVektor.clone()
			}
		];
		this.spieler.fügeBlockHinzu();
		this.spieler.fügeBlockHinzu();
		// Definiere Startposition von Spieler relativ zum Terrain
		this.spieler.blockListe[0].mesh.position.set(this.terrain.breite / 2, this.terrain.höhe / 2, this.terrain.dicke / 2);
		// Rotiere Kamera, damit es in die Richtung der Schlange guckt
		kamera.up.set(0, 1, 0);
		kamera.position.copy(this.spieler.blockListe[0].mesh.position);
		kamera.lookAt(this.spieler.blockListe[0].mesh.position.clone().add(this.spieler.richtungsVektor));
		szene.add(this.spieler.blockListe[0].mesh);
		this.spieler.aktivieresteuerung(this.terrain);
		// Punkte
		this.punkteZahl = 0;
		this.hud.änderePunkteZahl(this.punkteZahl);
		// Stats
		document.body.appendChild(this.stats.dom);
		// SpieleSchleife
		// Pauseoption
		document.addEventListener('keydown', this.pausiereSpiel);
		this.starteSpielSchleife();
	}

	private starteSpielSchleife = () => {
	// https://stackoverflow.com/questions/19764018/controlling-fps-with-requestanimationframe
		let aktuelleZeit = performance.now();
		let vergangeneZeit: number;
		const spielSchleife = (renderZeit: number) => {
			this.stats.begin();
			vergangeneZeit = renderZeit - aktuelleZeit;
			// Bewegt Spieler oder Kamera jede x Sekunden basierend aufs Spielerinterval und nur, wenn die letzte Animation schon fertig ist
			// Wenn die letze Animation nocn noch fertig ist, dann wird die weiter animiert
			if(vergangeneZeit > this.spieler.bewegungsInterval && !this.spieler.kameraStatus.animiertGerade) {
				// Beende die Schleife, wenn der Spieler verloren hat
				if(this.schleifeSollGestopptWerden) {
					this.schleifeSollGestopptWerden = false;
					return;
				}
				// Zufällige Chance, dass ein Apfel spawnt
				if(Math.random() < this.äpfelSpawner!.spawnwahrscheinlichkeit) {
					this.äpfelSpawner!.spawneApfel();
				}
				if(this.spieler.kameraStatus.mussRotiertWerden) {
					this.spieler.rotiereKameraParallelZurSchlange();
				}
				else {
					// Terrain als Arguments, damit es nach der Bewegung für Kollissionen schauen kann
					this.spieler.bewegeSpieler(this.guckeFürKollissionen);
				}
				aktuelleZeit = renderZeit - (vergangeneZeit % this.spieler.bewegungsInterval);
			} else {
				this.spieler.kameraTween.update();
			}
			this.äpfelSpawner!.rotiereÄpfel();
			renderer.render(szene, kamera);

			this.stats.end();
			requestAnimationFrame(spielSchleife);

		}
		requestAnimationFrame(spielSchleife);
	}

	public änderePunkteZahl = (nummerVonPunkten: number) => {
		this.punkteZahl += nummerVonPunkten;
		this.hud.änderePunkteZahl(this.punkteZahl);
		// Mache Spieler schneller
		this.spieler.bewegungsInterval /= 1.05;
	}

	private gameOver = () => {
		this.audioSpieler.spieleGameOverEffekt();
		this.spielerHatVerloren = true;
		this.schleifeSollGestopptWerden = true;
		this.spieler.mausSteuerung.unlock();
		document.removeEventListener('keydown', this.pausiereSpiel);
		const gameOverText = `Game over! Deine Punktzahl ist ${this.punkteZahl}`;
		this.hud.aufGameOver(gameOverText);
		document.body.removeChild(this.stats.dom);
	}

	private guckeFürKollissionen = () => {
		{
			const spielerPositionKopie = this.spieler.blockListe[0].mesh.position.clone();
			this.terrain.verarbeiteKollissionenMitSpieler(this.spieler);
			if(!spielerPositionKopie.equals(this.spieler.blockListe[0].mesh.position)) this.audioSpieler.spielePortalEffekt();
		}
		this.äpfelSpawner!.verarbeiteKollissionenMitSpieler(this.spieler, this.audioSpieler.spieleApfelEffekt);
		this.spieler.verarbeiteKollissionenMitSpieler(this.gameOver);
	}

	private pausiereSpiel = (key: KeyboardEvent) => {
		// Gucke, ob Spiel schon pausiert wurde
		if(key.code !== 'Space') {
			return;
		}
		if(this.hud.pausierungsText.classList.contains('inaktiv')) {
			this.schleifeSollGestopptWerden = true;
			this.hud.pausierungsText.classList.remove('inaktiv');
		} else {
			this.hud.pausierungsText.classList.add('inaktiv');
			this.starteSpielSchleife();
		}
	}
}